import { Injectable } from '@angular/core';
import { NavigationEnd, Router, Route } from '@angular/router';
import { Observable, BehaviorSubject, combineLatest, of, filter, switchMap } from 'rxjs';

import { MenuItem } from 'primeng/api';

import { AuthService } from '../authentication/auth.service';

import { SharedSettings } from '../models/shared-settings';

import { rootMenu } from '../root-menu';
import { BrowserProviderService } from './browser-provider.service';
import { SitesService } from './sites.service';

@Injectable({
    providedIn: 'root'
})
export class MenuService {
    private _fullMenu: BehaviorSubject<MenuItem[]>;
    private hasQualityControlAccess: boolean = false;

    get FullMenu(): Observable<MenuItem[]> {
        return this._fullMenu.asObservable().pipe(filter(menu => menu !== null));
    }

    constructor(private router: Router,
                private authService: AuthService,
                private browser: BrowserProviderService,
                private settings: SharedSettings,
                private siteService: SitesService) {

        this._fullMenu = new BehaviorSubject<MenuItem[]>(null);

        combineLatest([
            this.authService.Roles,
            this.siteService.hasAnyQualityControlSites()
        ]).subscribe(([roles, hasQC]) => {
            this.hasQualityControlAccess = hasQC;

            const menu = this.getFullMenu(roles || [], hasQC);
            this._fullMenu.next(menu);
        });
    }

    private getFullMenu(roles: string[], hasQualityControlAccess: boolean): MenuItem[] {
        const root = this.getRootMenu(roles);
        const app = this.getAppMenu(roles, hasQualityControlAccess);

        // workaround... items seems to magically appear on the root items...
        root.forEach(r => r.items = null);

        // clever parsing trick via https://j11y.io/javascript/parsing-urls-with-the-dom/
        const a = document.createElement('a');
        a.href = this.browser.GetLocation().href;
        const path = a.pathname;

        const currentRoot = root.slice().reverse().find(r => path.indexOf(r.url) === 0);

        currentRoot.items = app;
        return root;
    }

    private getRootMenu(roles: string[]): MenuItem[] {
        return rootMenu.filter(o => (!o.roles)
                                 || o.roles.length === 0
                                 || o.roles.some(or => roles.some(r => or === r)))
                        .filter(o => (!o.feature)
                                  || this.settings.features[o.feature]);
    }

    private flatten(routes: Route[]): Route[] {
        if (routes === undefined)
            return [];

        const children = routes.map(r => this.flatten(r.children)).flat(1);

        return [ ...routes, ...children ];
    }

    private find(needle: Route, haystack: Route[]): Route[] {
        if (!haystack || haystack.length === 0)
            return null;
        else if (haystack.includes(needle))
            return [needle];
        else
            return haystack
                .reduce((aggregate, h) => {
                    if (aggregate)
                        return aggregate;
                    const f = this.find(needle, h.children);
                    return f ? [h, ...f] : null;
                }, <Route[]> null);
    }

    private getAppMenu(roles: string[], hasQualityControlAccess: boolean): MenuItem[] {
        const flat = this.flatten(this.router.config);

        const routes = flat
            .filter(r => r.data ? r.data.menuOrder : null)
            .filter(route => roles.some(role => !route.data.roles || route.data.roles.includes(role)))
            .filter(r => !r.data || !r.data.feature || this.settings.features[r.data.feature])
            .filter(r => !r.data || !r.data.requiresQualityControlAccess || this.hasQualityControlAccess);

        routes.sort((l, r) => l.data.menuOrder - r.data.menuOrder);

        return routes.map(r => <MenuItem> {
            label: r.data.link || r.data.label,
            routerLink: '/' + this.find(r, this.router.config).map(n => n.path).join('/')
        });
    }

}
